import React, { useMemo, useState, useCallback } from "react";
import "./MaterialCalculator.css";

import { PRODUCTS, CATEGORY_FILTERS } from "./products";
import { ERRORS } from "./messages";
import { isValidEmail, parsePositive, calculateResult, validateInputs, buildWhatsAppMessage } from "./materialCalc";
import { submitToHubSpot } from "./hubspotClient";
import ProductThumb from "./ProductThumb";
import LeadCaptureForm from "./LeadCaptureForm";

/* ==========================================================================
   Calculadora de Material — Decototal
   Componente orquestador: estado + flujo. La lógica de cálculo, validación,
   HubSpot y el catálogo de productos viven en sus propios módulos dentro de
   esta misma carpeta.
   ========================================================================== */

const WHATSAPP_NUMBER = "525620863653";
const THANK_YOU_PATH = "/gracias/calculadora-materiales";

// ---------------------------------------------------------------------------
// Aviso: venta de material, sin servicio de instalación
// ---------------------------------------------------------------------------
function InstallationNotice() {
  return (
    <div className="dc-notice" role="note">
      <span className="dc-notice-icon" aria-hidden="true">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m7.5 4.27 9 5.15" />
          <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
          <path d="m3.3 7 8.7 5 8.7-5" />
          <path d="M12 22V12" />
        </svg>
      </span>
      <p className="dc-notice-text">
        <strong>Solo venta de producto.</strong>
        <span>No incluye instalación; consulta con tu contratista de confianza.</span>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
export function MaterialCalculator() {
  const [productId, setProductId] = useState(PRODUCTS[0].id);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [mode, setMode] = useState("area"); // "area" | "dim"
  const [waste, setWaste] = useState("with"); // "with" | "without"
  const [area, setArea] = useState("");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [wallWidth, setWallWidth] = useState("");
  const [wallHeight, setWallHeight] = useState("");
  const [leadName, setLeadName] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [formError, setFormError] = useState("");
  const [calcError, setCalcError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hubspotWarning, setHubspotWarning] = useState(""); // feedback no bloqueante

  const product = useMemo(
    () => PRODUCTS.find((p) => p.id === productId) || null,
    [productId]
  );

  const filteredProducts = useMemo(() => {
    if (!categoryFilter) return PRODUCTS;
    const f = CATEGORY_FILTERS.find((c) => c.id === categoryFilter);
    if (!f) return PRODUCTS;
    return PRODUCTS.filter((p) => f.matches.includes(p.category));
  }, [categoryFilter]);

  const computedArea = useMemo(() => {
    if (mode === "area") return parsePositive(area);
    return parsePositive(length) * parsePositive(width);
  }, [mode, area, length, width]);

  // Resultado en vivo
  const result = useMemo(
    () =>
      calculateResult(product, {
        waste,
        computedArea,
        wallWidth,
        wallHeight,
      }),
    [product, waste, computedArea, wallWidth, wallHeight]
  );

  // ¿Los datos del Paso 02 están completos?
  const inputsReady = useMemo(() => {
    return validateInputs(product, mode, area, length, width, wallWidth, wallHeight) === null;
  }, [product, mode, area, length, width, wallWidth, wallHeight]);

  const handleCategoryClick = useCallback((catId) => {
    setCategoryFilter((current) => (current === catId ? "" : catId));
  }, []);

  const handleCalculate = useCallback(() => {
    setCalcError("");
    setHubspotWarning("");

    const error = validateInputs(product, mode, area, length, width, wallWidth, wallHeight);
    if (error) {
      setCalcError(error);
      return;
    }

    // Scroll suave al resultado
    setTimeout(() => {
      const el = document.getElementById("result-card");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }, [product, mode, area, length, width, wallWidth, wallHeight]);

  const whatsappUrl = result
    ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
      buildWhatsAppMessage(result, leadName)
    )}`
    : "#";

  const handleLeadSubmit = useCallback(async () => {
    setFormError("");
    setHubspotWarning("");

    if (!result) {
      setFormError(ERRORS.NO_RESULT);
      return;
    }

    const name = leadName.trim();
    const email = leadEmail.trim();
    const phone = leadPhone.replace(/\D/g, "");

    if (!name) {
      setFormError(ERRORS.NO_NAME);
      return;
    }
    if (!isValidEmail(email)) {
      setFormError(ERRORS.INVALID_EMAIL);
      return;
    }
    if (phone.length !== 10) {
      setFormError(ERRORS.INVALID_PHONE);
      return;
    }

    const detalle =
      `Cotización desde la calculadora. Producto: ${result.product.name}. ` +
      `Área: ${result.baseArea.toFixed(2)} m². Cantidad: ${result.quantity} ${result.product.unit}.` +
      (result.isOrientable
        ? ` Muro: ${result.wallW.toFixed(2)} × ${result.wallH.toFixed(2)} m (instalación vertical).`
        : "");

    // Abrir la pestaña dentro del gesto del usuario evita que el navegador
    // bloquee WhatsApp mientras se espera la respuesta de HubSpot.
    const whatsappWindow = window.open("", "_blank");
    setSubmitting(true);

    try {
      const hsResult = await submitToHubSpot({
        name,
        email,
        phone,
        productId: result.product.id,
        productName: result.product.name,
        detalle,
      });

      if (!hsResult.ok) {
        // No bloqueamos al usuario: mostramos aviso y seguimos a WhatsApp
        setHubspotWarning(ERRORS.HUBSPOT_FAIL);
        console.warn("[Calculadora] HubSpot falló, se abre WhatsApp de todas formas", hsResult);
      }
    } catch (err) {
      // Por si acaso (aunque submitToHubSpot ya captura)
      setHubspotWarning(ERRORS.HUBSPOT_FAIL);
      console.error("[Calculadora] Error inesperado en submit", err);
    } finally {
      // dataLayer (si existe)
      const dataLayer = window.dataLayer;
      if (dataLayer) {
        dataLayer.push({
          event: "calculadora_conversion",
          formFormat: "hubspot_whatsapp",
          productName: result.product.name,
          calculatedArea: `${result.baseArea.toFixed(2)} m²`,
          calculatedUnits: `${result.quantity} ${result.product.unit}`,
        });
      }

      // Si el navegador bloqueó la pestaña, WhatsApp se abre en esta misma.
      if (whatsappWindow) {
        whatsappWindow.location.href = whatsappUrl;
        window.location.href = THANK_YOU_PATH;
      } else {
        window.location.href = whatsappUrl;
      }
      setSubmitting(false);
    }
  }, [result, leadName, leadEmail, leadPhone, whatsappUrl]);

  return (
    <div className="deco-calc">
      {/* Hero */}
      <section className="dc-hero">
        <div className="dc-badge">Calculadora de Material</div>
        <h1 className="dc-hero-title">
          Calcula lo justo. <br />
          <span className="dc-gold-text">Invierte con precisión.</span>
        </h1>
        <p className="dc-hero-sub">
          Estima al instante cuántas cajas, piezas o placas necesitas para tu proyecto, con el margen de merma que elijas.
        </p>
      </section>

      {/* Card principal */}
      <section className="dc-main">
        <div className="dc-card">
          <div className="dc-grid">
            {/* Formulario */}
            <div className="dc-form">
              {/* Paso 01 */}
              <div className="dc-step-head">
                <div className="dc-step-num">Paso 01</div>
                <h2 className="dc-step-title">Selecciona tu producto</h2>
              </div>

              <div className="dc-cats" role="tablist" aria-label="Filtrar productos por categoría">
                {CATEGORY_FILTERS.map((cat) => {
                  const active = cat.id === categoryFilter;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => handleCategoryClick(cat.id)}
                      className={`dc-cat-btn ${active ? "is-active" : ""}`}
                    >
                      {cat.label}
                    </button>
                  );
                })}
              </div>

              <div key={categoryFilter} className="dc-products dc-fade-in">
                {filteredProducts.map((p) => {
                  const active = p.id === productId;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setProductId(p.id)}
                      className={`dc-product ${active ? "is-active" : ""}`}
                    >
                      <ProductThumb product={p} />
                      <div className="dc-product-name">{p.name}</div>
                    </button>
                  );
                })}
              </div>

              {/* Paso 02 */}
              <div className="dc-step-head dc-step-head--gap">
                <div className="dc-step-num">Paso 02</div>
                <h2 className="dc-step-title">
                  {product?.orientable ? "Mide tu muro" : "Ingresa tu área"}
                </h2>
              </div>

              {product?.orientable ? (
                <>
                  <div className="dc-dim-grid dc-inputs">
                    <div>
                      <label htmlFor="dc-wall-width" className="dc-label">
                        Ancho del muro
                      </label>
                      <div className="dc-input-wrap">
                        <input
                          id="dc-wall-width"
                          type="number"
                          inputMode="decimal"
                          min="0"
                          placeholder="Ej. 3.00"
                          value={wallWidth}
                          onChange={(e) => setWallWidth(e.target.value)}
                          className="dc-input"
                        />
                        <span className="dc-input-suffix">m</span>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="dc-wall-height" className="dc-label">
                        Altura del muro
                      </label>
                      <div className="dc-input-wrap">
                        <input
                          id="dc-wall-height"
                          type="number"
                          inputMode="decimal"
                          min="0"
                          placeholder="Ej. 2.40"
                          value={wallHeight}
                          onChange={(e) => setWallHeight(e.target.value)}
                          className="dc-input"
                        />
                        <span className="dc-input-suffix">m</span>
                      </div>
                    </div>
                  </div>
                  <p className="dc-step-desc" style={{ marginTop: "0.75rem" }}>
                    Instalación vertical (de piso a techo). Los recortes de este producto no se
                    reutilizan; por eso el cálculo usa las medidas del muro para estimar las piezas.
                  </p>
                </>
              ) : (
                <>
                  <div className="dc-toggle">
                    {["area", "dim"].map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setMode(m)}
                        className={`dc-toggle-btn ${mode === m ? "is-active" : ""}`}
                      >
                        {m === "area" ? "Área total (m²)" : "Largo × Ancho"}
                      </button>
                    ))}
                  </div>

                  <div className="dc-inputs">
                    {mode === "area" ? (
                      <div>
                        <label htmlFor="dc-area" className="dc-label">
                          Superficie total
                        </label>
                        <div className="dc-input-wrap">
                          <input
                            id="dc-area"
                            type="number"
                            inputMode="decimal"
                            min="0"
                            placeholder="Ej. 25"
                            value={area}
                            onChange={(e) => setArea(e.target.value)}
                            className="dc-input"
                          />
                          <span className="dc-input-suffix">m²</span>
                        </div>
                      </div>
                    ) : (
                      <div className="dc-dim-grid">
                        <div>
                          <label htmlFor="dc-length" className="dc-label">
                            Largo
                          </label>
                          <div className="dc-input-wrap">
                            <input
                              id="dc-length"
                              type="number"
                              inputMode="decimal"
                              min="0"
                              placeholder="0.0"
                              value={length}
                              onChange={(e) => setLength(e.target.value)}
                              className="dc-input"
                            />
                            <span className="dc-input-suffix">m</span>
                          </div>
                        </div>
                        <div>
                          <label htmlFor="dc-width" className="dc-label">
                            Ancho
                          </label>
                          <div className="dc-input-wrap">
                            <input
                              id="dc-width"
                              type="number"
                              inputMode="decimal"
                              min="0"
                              placeholder="0.0"
                              value={width}
                              onChange={(e) => setWidth(e.target.value)}
                              className="dc-input"
                            />
                            <span className="dc-input-suffix">m</span>
                          </div>
                        </div>
                        {computedArea > 0 && (
                          <div className="dc-area-calc">
                            Área calculada:{" "}
                            <span className="dc-bold">{computedArea.toFixed(2)} m²</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Paso 03 */}
              <div className="dc-step-head dc-step-head--gap">
                <div className="dc-step-num">Paso 03</div>
                <h2 className="dc-step-title">Margen de desperdicio</h2>
                <p className="dc-step-desc">
                  Elige si deseas incluir un margen de seguridad para cortes y ajustes.
                </p>
              </div>

              <div className="dc-waste">
                {[
                  { id: "with", title: "Con Merma", sub: "+5% recomendado" },
                  { id: "without", title: "Sin Merma", sub: "Medida exacta" },
                ].map((opt) => {
                  const active = waste === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setWaste(opt.id)}
                      className={`dc-waste-btn ${active ? "is-active" : ""}`}
                    >
                      <span className={`dc-radio ${active ? "is-active" : ""}`}>
                        {active && <span className="dc-radio-dot" />}
                      </span>
                      <span>
                        <span className="dc-waste-title">{opt.title}</span>
                        <span className="dc-waste-sub">{opt.sub}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {calcError && (
                <div role="alert" className="dc-calc-error">
                  {calcError}
                </div>
              )}

              <button
                type="button"
                onClick={handleCalculate}
                className="dc-calc-btn"
                disabled={!inputsReady}
                aria-disabled={!inputsReady}
              >
                Calcular Material
              </button>
            </div>

            {/* Resultado */}
            <div id="result-card" className="dc-result">
              <div className="dc-result-glow" />
              <div className="dc-result-inner">
                <div className="dc-result-eyebrow">Resultado</div>
                <h2 className="dc-result-title">Tu cálculo</h2>

                {!result ? (
                  <>
                    <div className="dc-result-empty">
                      Selecciona un producto e ingresa la superficie de tu proyecto para ver el desglose recomendado.
                    </div>
                    <InstallationNotice />
                  </>
                ) : (
                  <div className="dc-result-body">
                    <div className="dc-result-product">
                      <div className="dc-result-thumb">
                        <ProductThumb product={result.product} size="sm" />
                      </div>
                      <div className="dc-result-product-meta">
                        <div className="dc-result-product-eyebrow">Producto seleccionado</div>
                        <div className="dc-result-product-name">{result.product.name}</div>
                      </div>
                    </div>

                    <div className="dc-qty">
                      <div className="dc-qty-label">Cantidad a comprar</div>
                      <div className="dc-qty-value">
                        <span className="dc-qty-number">{result.quantity}</span>
                        <span className="dc-qty-unit">{result.product.unit}</span>
                      </div>
                    </div>

                    <div className="dc-area-box">
                      <div className="dc-area-box-label">{result.areaLabel}</div>
                      <div className="dc-area-box-value">
                        {result.areaValue.toFixed(2)}{" "}
                        <span className="dc-area-box-unit">m²</span>
                      </div>
                      {result.isOrientable && (
                        <div className="dc-area-box-label" style={{ marginTop: "0.5rem" }}>
                          Muro: {result.wallW.toFixed(2)} × {result.wallH.toFixed(2)} m (
                          {result.baseArea.toFixed(2)} m²) · {result.runs} columnas ×{" "}
                          {result.perRun} pza
                        </div>
                      )}
                    </div>

                    <InstallationNotice />

                    <div className="dc-note">
                      <div className="dc-note-label">Especificación técnica</div>
                      <p className="dc-note-text">
                        <svg
                          className="dc-note-icon"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 16v-4" />
                          <path d="M12 8h.01" />
                        </svg>
                        <span>{result.product.note}</span>
                      </p>
                    </div>

                    <LeadCaptureForm
                      leadName={leadName}
                      setLeadName={setLeadName}
                      leadEmail={leadEmail}
                      setLeadEmail={setLeadEmail}
                      leadPhone={leadPhone}
                      setLeadPhone={setLeadPhone}
                      formError={formError}
                      hubspotWarning={hubspotWarning}
                      submitting={submitting}
                      onSubmit={handleLeadSubmit}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <p className="dc-footnote">
          Puedes activar un 5% de merma recomendado para cortes y ajustes. Todas las cantidades se
          redondean a la unidad completa superior. Decototal comercializa material; no incluye
          servicio de instalación.
        </p>
      </section>
    </div>
  );
}

export default MaterialCalculator;